/**
 * DailyEntriesTab — Daily Financial Entry, Fixed Expense Snapshots, Monthly View
 *
 * Self-contained:
 *  - Owns entryDate, income, expense, editingFinancialId state.
 *  - Owns fixed-expense snapshot resolution + dialog state.
 *  - Renders the daily entry form, daily profitability cards,
 *    workshop-ops chart, monthly view table + heat-map.
 *
 * Extracted from FinancialsTab.jsx (Feb 2026 refactor — iter138.9al).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Plus, Trash2, TrendingUp, ArrowUp, ArrowDown, Edit2, Layers, Calendar, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import FixedExpenseSnapshots from '../FixedExpenseSnapshots';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount || 0);

// iter181 — cents-precision formatter for the Parts TO / Labor TO split
// (tyre flat-rates like $40 + $75.40 need the decimals to read correctly).
const formatCurrency2 = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function DailyEntriesTab() {
  const today = useMemo(() => new Date(), []);
  const todayLocal = toLocalDateStr(today);

  const [entryDate, setEntryDate] = useState(todayLocal);
  const [systemTurnover, setSystemTurnover] = useState(0);
  const [systemPartsOrp, setSystemPartsOrp] = useState(0);
  const [systemPartsLinesCount, setSystemPartsLinesCount] = useState(0);
  const [income, setIncome] = useState({ turnover: 0, banking: 0, cash: 0, transfer: 0 });
  const [expense, setExpense] = useState({ parts: 0, entertainment: 0, others: 0, cash: 0 });
  const [saving, setSaving] = useState(false);
  const [editingFinancialId, setEditingFinancialId] = useState(null);

  const [snapshotsDialogOpen, setSnapshotsDialogOpen] = useState(false);
  const [dailyFixedExpense, setDailyFixedExpense] = useState(0);
  const [snapshotInfo, setSnapshotInfo] = useState(null);

  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [, setFixedExpensesTotal] = useState(0);
  const [, setFixedExpenses] = useState([]);

  const fetchEntries = useCallback(async () => {
    setEntriesLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/super-admin/financials/daily?page_size=400`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  const fetchFixedExpenses = useCallback(async () => {
    try {
      const dt = entryDate ? new Date(entryDate + 'T00:00:00') : today;
      const params = new URLSearchParams({ month: String(dt.getMonth() + 1), year: String(dt.getFullYear()) });
      const response = await fetch(`${BACKEND_URL}/api/super-admin/financials/fixed-expenses?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFixedExpenses(data.items || []);
        setFixedExpensesTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch fixed expenses:', error);
    }
  }, [entryDate, today]);

  const fetchDailyFixedExpense = useCallback(async () => {
    if (!entryDate) return;
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/super-admin/financials/fixed-expense-snapshots/effective?on=${entryDate}`,
        { credentials: 'include' },
      );
      if (r.ok) {
        const data = await r.json();
        setDailyFixedExpense(Number(data.daily_amount) || 0);
        setSnapshotInfo(data.snapshot || null);
      } else {
        setDailyFixedExpense(0);
        setSnapshotInfo(null);
      }
    } catch (_e) {
      setDailyFixedExpense(0);
      setSnapshotInfo(null);
    }
  }, [entryDate]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchFixedExpenses(); }, [fetchFixedExpenses]);
  useEffect(() => { fetchDailyFixedExpense(); }, [fetchDailyFixedExpense]);

  useEffect(() => {
    const fetchTurnover = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/super-admin/financials/daily-turnover/${entryDate}`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setSystemTurnover(data.turnover || 0);
          setSystemPartsOrp(data.parts_orp_total || 0);
          setSystemPartsLinesCount(data.lines_with_orp || 0);
          if (!editingFinancialId) {
            setIncome(prev => ({ ...prev, turnover: data.turnover || 0 }));
            setExpense(prev => ({ ...prev, parts: data.parts_orp_total || 0 }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch turnover:', error);
      }
    };
    if (entryDate) fetchTurnover();
  }, [entryDate, editingFinancialId]);

  const handleSaveEntry = async () => {
    setSaving(true);
    try {
      const payload = {
        date: entryDate,
        turnover: parseFloat(income.turnover) || 0,
        banking: parseFloat(income.banking) || 0,
        income_cash: parseFloat(income.cash) || 0,
        transfer: parseFloat(income.transfer) || 0,
        expense_parts: parseFloat(expense.parts) || 0,
        expense_entertainment: parseFloat(expense.entertainment) || 0,
        expense_others: parseFloat(expense.others) || 0,
        expense_cash: parseFloat(expense.cash) || 0,
      };
      const url = editingFinancialId
        ? `${BACKEND_URL}/api/super-admin/financials/daily/${editingFinancialId}`
        : `${BACKEND_URL}/api/super-admin/financials/daily`;
      const response = await fetch(url, {
        method: editingFinancialId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success(editingFinancialId ? 'Entry updated' : 'Entry saved');
        fetchEntries();
        setIncome({ turnover: systemTurnover, banking: 0, cash: 0, transfer: 0 });
        setExpense({ parts: systemPartsOrp, entertainment: 0, others: 0, cash: 0 });
        setEditingFinancialId(null);
      } else {
        toast.error('Failed to save entry');
      }
    } catch (_error) {
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingFinancialId(entry.financial_id);
    setEntryDate(entry.date);
    setIncome({
      turnover: entry.turnover || 0,
      banking: entry.banking || 0,
      cash: entry.income_cash || 0,
      transfer: entry.transfer || 0,
    });
    setExpense({
      parts: entry.expense_parts || 0,
      entertainment: entry.expense_entertainment || 0,
      others: entry.expense_others || 0,
      cash: entry.expense_cash || 0,
    });
    const el = document.getElementById('daily-entry-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCancelEdit = () => {
    setEditingFinancialId(null);
    setIncome({ turnover: systemTurnover, banking: 0, cash: 0, transfer: 0 });
    setExpense({ parts: systemPartsOrp, entertainment: 0, others: 0, cash: 0 });
  };

  const handleDeleteEntry = async (financialId) => {
    if (!confirm('Delete this entry?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/super-admin/financials/daily/${financialId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Entry deleted');
        fetchEntries();
      } else {
        toast.error('Failed to delete entry');
      }
    } catch (_error) {
      toast.error('Failed to delete entry');
    }
  };

  const incomeTotal = (parseFloat(income.banking) || 0) + (parseFloat(income.cash) || 0) + (parseFloat(income.transfer) || 0);
  const expenseTotal = (parseFloat(expense.parts) || 0) + (parseFloat(expense.entertainment) || 0) + (parseFloat(expense.others) || 0) + (parseFloat(expense.cash) || 0);

  const entriesByMonth = useMemo(() => {
    const groups = {};
    (entries || []).forEach(e => {
      if (!e.date) return;
      const key = e.date.slice(0, 7);
      if (!groups[key]) groups[key] = [];
      groups[key].push(e);
    });
    return groups;
  }, [entries]);

  const monthKeys = useMemo(() => {
    const set = new Set(Object.keys(entriesByMonth));
    set.add(todayLocal.slice(0, 7));
    if (entryDate) set.add(entryDate.slice(0, 7));
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [entriesByMonth, todayLocal, entryDate]);

  const activeMonthTab = entryDate ? entryDate.slice(0, 7) : todayLocal.slice(0, 7);

  const formatMonthLabel = (key) => {
    const [y, m] = key.split('-').map(Number);
    return `${MONTH_NAMES[m - 1]} ${y}`;
  };

  const handleAddEntryForMonth = (monthKey) => {
    const todayKey = todayLocal.slice(0, 7);
    if (monthKey === todayKey) setEntryDate(todayLocal);
    else setEntryDate(`${monthKey}-01`);
    const el = document.getElementById('daily-entry-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleMonthTabChange = (monthKey) => {
    const todayKey = todayLocal.slice(0, 7);
    if (monthKey === todayKey) setEntryDate(todayLocal);
    else setEntryDate(`${monthKey}-01`);
  };

  return (
    <div className="space-y-6" data-testid="daily-entries-tab">
      {/* Top header (month tabs + FE versions button + effective FE badge) */}
      <Card className="p-4 border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white sticky top-0 z-20 shadow-sm" data-testid="entries-top-header">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-600" />
              Daily Entries
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Pick a month tab — entry form, fixed-expense badge & monthly view all retarget to the selected month.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setSnapshotsDialogOpen(true)}
            className="h-9 border-orange-300 bg-orange-50/40 hover:bg-orange-100 text-orange-800"
            data-testid="open-fixed-expense-snapshots-btn-top"
          >
            <Layers className="h-4 w-4 mr-1.5" />
            Fixed Expense
            <span className="ml-1.5 text-[10px] uppercase tracking-wider font-bold bg-orange-200 text-orange-900 px-1.5 py-0.5 rounded-full">
              versions
            </span>
          </Button>
        </div>

        {monthKeys.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="entries-month-tabs-top">
            <span className="text-xs text-slate-500 font-medium whitespace-nowrap mr-1">Month:</span>
            {monthKeys.map((key) => {
              const active = key === activeMonthTab;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleMonthTabChange(key)}
                  data-testid={`entries-month-tab-top-${key}`}
                  className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors flex-shrink-0 ${
                    active ? 'bg-blue-600 text-white font-semibold shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {formatMonthLabel(key)}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="entries-effective-fe-badge">
          {snapshotInfo ? (
            <span className="text-xs text-orange-800 bg-orange-50 border border-orange-200 px-2 py-1 rounded inline-flex items-center gap-1.5">
              <Layers className="h-3 w-3" />
              <strong>Fixed Expense for {formatMonthLabel(activeMonthTab)}:</strong>
              Version {snapshotInfo.version_no} — {formatCurrency(snapshotInfo.monthly_total)}/month
              <span className="text-orange-600">(effective {new Date(snapshotInfo.effective_from + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })})</span>
            </span>
          ) : (
            <span className="text-xs text-amber-800 bg-amber-50 border border-amber-300 px-2 py-1 rounded inline-flex items-center gap-1.5">
              <AlertCircle className="h-3 w-3" />
              No Fixed Expense version was effective in {formatMonthLabel(activeMonthTab)}.
              Click <strong>Fixed Expense</strong> above to create one (backdate to {activeMonthTab}-01 if needed).
            </span>
          )}
        </div>
      </Card>

      {/* Daily Entry Form */}
      <Card className={`p-6 ${editingFinancialId ? 'border-2 border-blue-400 bg-blue-50/30' : ''}`} id="daily-entry-form">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-semibold text-slate-900">
            {editingFinancialId ? `Editing entry for ${entryDate}` : 'Daily Financial Entry'}
          </h3>
          {editingFinancialId && (
            <Button variant="outline" size="sm" onClick={handleCancelEdit} data-testid="cancel-edit-btn">
              Cancel edit
            </Button>
          )}
        </div>

        <div className="mb-6">
          <Label>Date</Label>
          <Input
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            className="w-[200px]"
            data-testid="entry-date-input"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income */}
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
              <ArrowUp className="h-4 w-4" /> Income
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-green-700">Turnover (System)</Label>
                <Input type="number" value={income.turnover} onChange={(e) => setIncome({ ...income, turnover: e.target.value })} placeholder="0" />
                <p className="text-xs text-green-600 mt-1">System: ${systemTurnover.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-xs text-green-700">Banking</Label>
                <Input type="number" value={income.banking} onChange={(e) => setIncome({ ...income, banking: e.target.value })} placeholder="0" />
              </div>
              <div>
                <Label className="text-xs text-green-700">Cash</Label>
                <Input type="number" value={income.cash} onChange={(e) => setIncome({ ...income, cash: e.target.value })} placeholder="0" />
              </div>
              <div>
                <Label className="text-xs text-green-700">Transfer</Label>
                <Input type="number" value={income.transfer} onChange={(e) => setIncome({ ...income, transfer: e.target.value })} placeholder="0" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-green-300 flex justify-between items-center">
              <span className="font-medium text-green-700">Total Income</span>
              <span className="text-xl font-bold text-green-900">{formatCurrency(incomeTotal)}</span>
            </div>
          </div>

          {/* Workshop Expense */}
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-red-800 flex items-center gap-2">
                <ArrowDown className="h-4 w-4" /> Workshop Expense
              </h4>
              {snapshotInfo ? (
                <span
                  className="text-xs text-orange-700 bg-orange-50 border border-orange-200 px-2 py-1 rounded cursor-help"
                  title={`Fixed Expense v${snapshotInfo.version_no} (effective ${snapshotInfo.effective_from}). Monthly $${(snapshotInfo.monthly_total || 0).toLocaleString('en-AU')} spread across working days (Mon–Fri) only. Weekends = $0.`}
                  data-testid="daily-fe-badge"
                >
                  Fixed Expense {new Date(entryDate + 'T00:00:00').getDay() === 0 || new Date(entryDate + 'T00:00:00').getDay() === 6
                    ? 'today (weekend = $0)'
                    : 'per working day'}: <strong>{formatCurrency(dailyFixedExpense)}</strong>
                </span>
              ) : (
                <span
                  className="text-xs text-amber-700 bg-amber-50 border border-amber-300 px-2 py-1 rounded"
                  title="No Fixed Expense version has been created yet."
                  data-testid="daily-fe-badge-empty"
                >
                  No Fixed Expense version set
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-red-700">Parts</Label>
                  {systemPartsLinesCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setExpense(p => ({ ...p, parts: systemPartsOrp }))}
                      className="text-[10px] text-blue-700 hover:underline"
                      title="Recalculate Parts expense from sum of ORP × qty across all invoices on this date"
                      data-testid="parts-reset-from-system"
                    >
                      ↻ Auto-fill from invoices (${systemPartsOrp.toFixed(2)})
                    </button>
                  )}
                </div>
                <Input
                  type="number"
                  value={expense.parts}
                  onChange={(e) => setExpense({ ...expense, parts: e.target.value })}
                  placeholder="0"
                  data-testid="expense-parts-input"
                />
                {systemPartsLinesCount > 0 && (
                  <p className="text-[10px] text-red-700/70 mt-0.5">
                    System ORP: <strong>${systemPartsOrp.toFixed(2)}</strong> from {systemPartsLinesCount} line item{systemPartsLinesCount === 1 ? '' : 's'} (auto-populated · editable)
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs text-red-700">Entertainment</Label>
                <Input type="number" value={expense.entertainment} onChange={(e) => setExpense({ ...expense, entertainment: e.target.value })} placeholder="0" />
              </div>
              <div>
                <Label className="text-xs text-red-700">Others</Label>
                <Input type="number" value={expense.others} onChange={(e) => setExpense({ ...expense, others: e.target.value })} placeholder="0" />
              </div>
              <div>
                <Label className="text-xs text-red-700">Cash</Label>
                <Input type="number" value={expense.cash} onChange={(e) => setExpense({ ...expense, cash: e.target.value })} placeholder="0" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-red-300 flex justify-between items-center">
              <span className="font-medium text-red-700">Total Workshop Expense</span>
              <span className="text-xl font-bold text-red-900">{formatCurrency(expenseTotal)}</span>
            </div>
          </div>
        </div>

        {/* Daily Profitability */}
        <div className="mt-6" data-testid="entry-day-breakdown">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-extrabold mb-2 flex items-center gap-2">
            <Calendar className="h-3 w-3" />
            Daily Profitability — {new Date(entryDate + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200 rounded-xl p-3" data-testid="day-income-card">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Income today</p>
              <p className="text-2xl font-black tabular-nums text-emerald-900 mt-1">{formatCurrency(incomeTotal)}</p>
              <p className="text-[10px] text-slate-500 mt-1">Banking + Cash + Transfer</p>
            </div>
            <div className="bg-gradient-to-br from-rose-50 to-white border-2 border-rose-200 rounded-xl p-3" data-testid="day-workshop-card">
              <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">Workshop expense today</p>
              <p className="text-2xl font-black tabular-nums text-rose-900 mt-1">{formatCurrency(expenseTotal)}</p>
              <p className="text-[10px] text-slate-500 mt-1">Parts + Entertainment + Others + Cash</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 rounded-xl p-3" data-testid="day-fixed-card">
              <p className="text-[10px] uppercase tracking-wider text-orange-700 font-bold">Fixed expense today</p>
              <p className="text-2xl font-black tabular-nums text-orange-900 mt-1">{formatCurrency(dailyFixedExpense)}</p>
              <p className="text-[10px] text-slate-500 mt-1">
                {snapshotInfo ? `v${snapshotInfo.version_no} · monthly ÷ working days (Mon–Fri)` : 'No version set — click Fixed Expense above'}
              </p>
            </div>
            {(() => {
              const dayNet = incomeTotal - expenseTotal - dailyFixedExpense;
              const positive = dayNet >= 0;
              return (
                <div
                  className={`bg-gradient-to-br ${positive ? 'from-blue-50 to-white border-blue-300' : 'from-amber-50 to-white border-amber-300'} border-2 rounded-xl p-3`}
                  data-testid="day-net-card"
                >
                  <p className={`text-[10px] uppercase tracking-wider font-bold ${positive ? 'text-blue-700' : 'text-amber-700'}`}>Net {positive ? 'profit' : 'loss'} today</p>
                  <p className={`text-2xl font-black tabular-nums mt-1 ${positive ? 'text-blue-900' : 'text-amber-900'}`} data-testid="day-net-amount">
                    {positive ? '' : '−'}{formatCurrency(Math.abs(dayNet))}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-1">Income − Workshop − Fixed</p>
                </div>
              );
            })()}
          </div>

          <div className="flex items-center justify-end">
            <Button onClick={handleSaveEntry} disabled={saving} size="lg" data-testid="save-entry-btn">
              <Plus className="h-4 w-4 mr-2" />
              {saving ? 'Saving…' : (editingFinancialId ? 'Update Entry' : 'Save Daily Entry')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Entries Table */}
      <Card className="p-6" data-testid="recent-entries-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" data-testid="entries-monthly-view-title">
              Daily Entries — {activeMonthTab ? formatMonthLabel(activeMonthTab) : 'Monthly View'}
            </h3>
            <p className="text-sm text-slate-500">Switch months from the tabs at the top. Click &quot;+ New Entry&quot; to add an entry to this month.</p>
          </div>
        </div>

        {entriesLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-blue-600 border-t-transparent"></div>
          </div>
        ) : monthKeys.length === 0 ? (
          <div className="text-center py-8 text-slate-500">No entries yet</div>
        ) : (
          (() => {
            const key = activeMonthTab;
            const monthEntries = (entriesByMonth[key] || []).slice().sort((a, b) => (b.date || '').localeCompare(a.date || ''));
            const monthTotals = monthEntries.reduce((acc, e) => ({
              turnover: acc.turnover + (e.turnover || 0),
              banking: acc.banking + ((e.banking || 0) + (e.income_cash || 0) + (e.transfer || 0)),
              expense: acc.expense + (e.expense_total || 0),
              fixed: acc.fixed + (e.fixed_expense_daily || 0),
              net: acc.net + (typeof e.net_with_fixed_expense === 'number' ? e.net_with_fixed_expense : (e.net_profit_loss || 0)),
            }), { turnover: 0, banking: 0, expense: 0, fixed: 0, net: 0 });

            const fixedMonthly = monthEntries[0]?.fixed_expense_monthly_total || 0;
            const workingDays = monthEntries[0]?.working_days_in_month || 0;

            return (
              <div className="mt-4" data-testid={`entries-month-content-${key}`}>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 flex-1">
                    <div className="p-2 bg-blue-50 rounded">
                      <p className="text-[10px] text-blue-600">Turnover (entered)</p>
                      <p className="text-sm font-bold text-blue-900">{formatCurrency(monthTotals.turnover)}</p>
                    </div>
                    <div className="p-2 bg-green-50 rounded">
                      <p className="text-[10px] text-green-600">Banking total</p>
                      <p className="text-sm font-bold text-green-900">{formatCurrency(monthTotals.banking)}</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded">
                      <p className="text-[10px] text-red-600">Workshop expense</p>
                      <p className="text-sm font-bold text-red-900">{formatCurrency(monthTotals.expense)}</p>
                    </div>
                    <div className="p-2 bg-orange-50 rounded" title={fixedMonthly ? `Monthly fixed ${formatCurrency(fixedMonthly)} ÷ ${workingDays} working days` : 'No fixed expense set'}>
                      <p className="text-[10px] text-orange-600">Fixed expense (allocated)</p>
                      <p className="text-sm font-bold text-orange-900">{formatCurrency(monthTotals.fixed)}</p>
                    </div>
                    <div className={`p-2 rounded ${monthTotals.net >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                      <p className={`text-[10px] ${monthTotals.net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>Net P&L (after fixed)</p>
                      <p className={`text-sm font-bold ${monthTotals.net >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>{formatCurrency(monthTotals.net)}</p>
                    </div>
                  </div>
                  <Button onClick={() => handleAddEntryForMonth(key)} size="sm" data-testid={`add-entry-${key}`}>
                    <Plus className="h-4 w-4 mr-1" /> New Entry
                  </Button>
                </div>

                {fixedMonthly > 0 && workingDays > 0 && (() => {
                  const breakEven = fixedMonthly / workingDays;
                  const entryByDate = new Map();
                  monthEntries.forEach(e => { if (e.date) entryByDate.set(e.date, e); });
                  const [yStr, mStr] = key.split('-');
                  const yInt = parseInt(yStr, 10);
                  const mInt = parseInt(mStr, 10);
                  const daysInMonth = new Date(yInt, mInt, 0).getDate();
                  const firstDow = new Date(yInt, mInt - 1, 1).getDay();
                  const offset = (firstDow + 6) % 7;
                  const cells = [];
                  for (let i = 0; i < offset; i++) cells.push({ blank: true });
                  const weekdayStats = Array(7).fill(null).map(() => ({ total: 0, profitable: 0, sum_net: 0 }));
                  for (let d = 1; d <= daysInMonth; d++) {
                    const dt = new Date(yInt, mInt - 1, d);
                    const iso = `${yStr}-${mStr}-${String(d).padStart(2, '0')}`;
                    const dow = (dt.getDay() + 6) % 7;
                    const entry = entryByDate.get(iso);
                    const isWeekend = dow >= 5;
                    let status = 'empty';
                    let net = null;
                    if (entry) {
                      net = typeof entry.net_with_fixed_expense === 'number'
                        ? entry.net_with_fixed_expense
                        : (entry.net_profit_loss || 0) - (entry.fixed_expense_daily || 0);
                      if (net >= 0) status = 'profit';
                      else if (net >= -(breakEven * 0.25)) status = 'near';
                      else status = 'loss';
                      weekdayStats[dow].total += 1;
                      weekdayStats[dow].sum_net += net;
                      if (net >= 0) weekdayStats[dow].profitable += 1;
                    }
                    cells.push({ day: d, dow, isWeekend, status, net, iso });
                  }
                  while (cells.length % 7 !== 0) cells.push({ blank: true });
                  const weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                  const statusColor = (s) => ({
                    profit: 'bg-emerald-500 text-white',
                    near: 'bg-amber-400 text-amber-900',
                    loss: 'bg-red-500 text-white',
                    empty: 'bg-slate-100 text-slate-400',
                  }[s]);
                  const weekdayColor = (s) => {
                    if (s.total === 0) return 'bg-slate-50 text-slate-400';
                    const r = s.profitable / s.total;
                    if (r >= 0.75) return 'bg-emerald-50 text-emerald-800 border-emerald-200';
                    if (r >= 0.4) return 'bg-amber-50 text-amber-800 border-amber-200';
                    return 'bg-red-50 text-red-800 border-red-200';
                  };
                  return (
                    <div className="mb-3 p-3 border rounded-lg bg-white" data-testid={`heatmap-${key}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Break-even Heat-map</p>
                          <p className="text-[11px] text-slate-500">Green = cleared break-even, Amber = within 25% of break-even, Red = below, Grey = no entry</p>
                        </div>
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500 rounded"></span> Profit</span>
                          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-400 rounded"></span> Near</span>
                          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-500 rounded"></span> Loss</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {weekdayLabels.map(wl => (
                          <div key={wl} className="text-[10px] text-center text-slate-500 font-medium uppercase py-1">{wl}</div>
                        ))}
                        {cells.map((c, idx) => {
                          if (c.blank) return <div key={`b${idx}`} className="h-10"></div>;
                          return (
                            <div
                              key={c.iso}
                              className={`h-10 rounded flex flex-col items-center justify-center text-[10px] font-medium ${statusColor(c.status)} ${c.isWeekend && c.status === 'empty' ? 'opacity-50' : ''}`}
                              title={`${c.iso}${c.net !== null ? ` — Net ${formatCurrency(c.net)}` : ''}`}
                            >
                              <span className="text-sm leading-none font-bold">{c.day}</span>
                              {c.net !== null && (
                                <span className="text-[9px] opacity-90">{c.net >= 0 ? '+' : '−'}{formatCurrency(Math.abs(c.net)).replace('$', '$').replace('.00', '')}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-7 gap-1 mt-3 pt-3 border-t">
                        {weekdayStats.map((s, i) => (
                          <div key={i} className={`p-1.5 border rounded text-center ${weekdayColor(s)}`}>
                            <p className="text-[10px] font-semibold uppercase">{weekdayLabels[i]}</p>
                            {s.total > 0 ? (
                              <>
                                <p className="text-[13px] font-bold leading-tight">{s.profitable}/{s.total}</p>
                                <p className="text-[9px] opacity-80">avg {formatCurrency(s.sum_net / s.total)}</p>
                              </>
                            ) : (<p className="text-[10px] opacity-60">—</p>)}
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-500 mt-2">
                        Per-weekday bottom strip: profitable days / total recorded + average net. Helps spot patterns (e.g. Saturdays consistently outperform).
                      </p>
                    </div>
                  );
                })()}

                {fixedMonthly > 0 && workingDays > 0 && (() => {
                  const breakEven = fixedMonthly / workingDays;
                  const profitable = monthEntries.filter(e => {
                    const n = typeof e.net_with_fixed_expense === 'number'
                      ? e.net_with_fixed_expense
                      : (e.net_profit_loss || 0) - (e.fixed_expense_daily || 0);
                    return n >= 0;
                  }).length;
                  const totalEntries = monthEntries.length;
                  const pct = totalEntries > 0 ? Math.round((profitable / totalEntries) * 100) : 0;
                  const allProfit = profitable === totalEntries && totalEntries > 0;
                  return (
                    <div className="mb-3 p-3 border rounded-lg bg-gradient-to-r from-slate-50 to-blue-50" data-testid={`breakeven-${key}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg shadow-sm">
                            <TrendingUp className={`h-5 w-5 ${allProfit ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600'}`} />
                          </div>
                          <div>
                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Daily Break-even</p>
                            <p className="text-base font-bold text-slate-900">{formatCurrency(breakEven)}/day</p>
                            <p className="text-[11px] text-slate-500">Each day must earn this much to cover fixed costs</p>
                          </div>
                        </div>
                        <div className="flex-1 max-w-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-600">
                              <span className="font-semibold text-emerald-700">{profitable}</span>
                              <span className="text-slate-500"> of </span>
                              <span className="font-semibold text-slate-900">{totalEntries}</span>
                              <span className="text-slate-500"> days profitable</span>
                            </span>
                            <span className={`text-xs font-bold ${allProfit ? 'text-emerald-700' : pct >= 50 ? 'text-amber-700' : 'text-red-700'}`}>
                              {pct}%
                            </span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full transition-all ${allProfit ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                          {totalEntries > 0 && (
                            <p className="text-[10px] text-slate-500 mt-1">
                              {totalEntries - profitable > 0
                                ? `${totalEntries - profitable} day(s) below break-even — revenue didn't cover allocated fixed costs.`
                                : `Every recorded day cleared break-even. Great month!`}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {fixedMonthly > 0 && workingDays > 0 && (
                  <p className="text-xs text-slate-500 mb-2">
                    ℹ️ Fixed Expense per day = {formatCurrency(fixedMonthly)} (monthly) ÷ {workingDays} working days = <strong>{formatCurrency(fixedMonthly / workingDays)}/day</strong>. Auto-applied to every entry in this month.
                  </p>
                )}

                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right text-violet-600" title="Parts turnover — parts portion of all invoice line items for this day (auto from invoices). For tyres, the per-tyre flat fitting rate is treated as labour and the rest as parts.">Parts TO</TableHead>
                      <TableHead className="text-right text-sky-600" title="Labour turnover — labour portion of all invoice line items for this day, incl. the per-tyre fitting flat-rate (auto from invoices).">Labor TO</TableHead>
                      <TableHead className="text-right">Turnover</TableHead>
                      <TableHead className="text-right">Banking</TableHead>
                      <TableHead className="text-right">Cash</TableHead>
                      <TableHead className="text-right">Transfer</TableHead>
                      <TableHead className="text-right text-red-600">Workshop Exp</TableHead>
                      <TableHead className="text-right text-orange-600">Fixed Exp / day</TableHead>
                      <TableHead className="text-right">Net P&L</TableHead>
                      <TableHead className="w-16">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthEntries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-6 text-slate-500">
                          No entries for {formatMonthLabel(key)} yet. Click &quot;+ New Entry&quot; to add one.
                        </TableCell>
                      </TableRow>
                    ) : monthEntries.map(entry => {
                      const netVal = typeof entry.net_with_fixed_expense === 'number'
                        ? entry.net_with_fixed_expense
                        : (entry.net_profit_loss || 0);
                      return (
                        <TableRow key={entry.financial_id} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{entry.date}</TableCell>
                          <TableCell className="text-right text-violet-700 tabular-nums" data-testid={`parts-to-${entry.financial_id}`}>{formatCurrency2(entry.parts_to)}</TableCell>
                          <TableCell className="text-right text-sky-700 tabular-nums" data-testid={`labor-to-${entry.financial_id}`}>{formatCurrency2(entry.labour_to)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.turnover)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.banking)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.income_cash)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.transfer)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(entry.expense_total)}</TableCell>
                          <TableCell className="text-right text-orange-600" title={entry.fixed_expense_monthly_total ? `${formatCurrency(entry.fixed_expense_monthly_total)} ÷ ${entry.working_days_in_month} days` : ''}>
                            {formatCurrency(entry.fixed_expense_daily || 0)}
                          </TableCell>
                          <TableCell className={`text-right font-semibold ${netVal >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {netVal >= 0 ? '+' : ''}{formatCurrency(netVal)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button size="icon" variant="ghost" onClick={() => handleEditEntry(entry)} data-testid={`edit-entry-${entry.financial_id}`} title="Edit entry">
                                <Edit2 className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => handleDeleteEntry(entry.financial_id)} data-testid={`delete-entry-${entry.financial_id}`} title="Delete entry">
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            );
          })()
        )}
      </Card>

      {/* Fixed Expense Snapshots Dialog */}
      <FixedExpenseSnapshots
        open={snapshotsDialogOpen}
        onClose={() => setSnapshotsDialogOpen(false)}
        defaultEffectiveFrom={(() => {
          const todayKey = todayLocal.slice(0, 7);
          if (activeMonthTab && activeMonthTab !== todayKey) {
            return `${activeMonthTab}-01`;
          }
          return todayLocal;
        })()}
        onSaved={() => {
          fetchEntries();
          fetchFixedExpenses();
          fetchDailyFixedExpense();
        }}
      />
    </div>
  );
}
